import { getLogger } from '@trimble/logging';
import Rox, { type RoxContext } from 'rox-browser';

// Compared by value: the rox-browser enum is not available when the module is mocked.
const FETCH_FAILED_STATUS = 'ERROR_FETCH_FAILED';

interface RoxDynamicApi {
  isEnabled(name: string, defaultValue: boolean): boolean;
}

// Rox is a page-wide singleton: it must only be set up once, and it accepts a single
// configurationFetchedHandler, so every provider instance shares this module's state.
let setupPromise: Promise<void> | undefined;
const fetchedListeners = new Set<() => void>();

export const setupRox = (apiKey: string): Promise<void> => {
  if (!setupPromise) {
    const setup: Promise<unknown> = Rox.setup(apiKey, {
      configurationFetchedHandler: (result) => {
        // Rox swallows fetch failures (blocked host, empty body), leaving every flag off.
        if (String(result.fetcherStatus) === FETCH_FAILED_STATUS) {
          getLogger().error(
            `CloudBees configuration fetch failed: ${
              result.errorDetails ?? 'no error details'
            }`
          );
          return;
        }
        // Cached and embedded configurations report hasChanges: false but still
        // define the flag values, so re-evaluate on every successful apply.
        fetchedListeners.forEach((listener) => listener());
      },
    });
    setupPromise = setup.then(
      () => undefined,
      (error: unknown) => {
        setupPromise = undefined;
        throw error;
      }
    );
  }
  return setupPromise;
};

export const onRoxConfigurationFetched = (
  listener: () => void
): (() => void) => {
  fetchedListeners.add(listener);
  return () => fetchedListeners.delete(listener);
};

export const setRoxContext = (context: RoxContext): void => {
  Rox.setContext(context);
};

export const isRoxFlagEnabled = (label: string): boolean => {
  // rox-browser types dynamicApi as any.
  const dynamicApi: RoxDynamicApi = Rox.dynamicApi;
  return dynamicApi.isEnabled(label, false);
};

export const resetRoxClient = (): void => {
  setupPromise = undefined;
  fetchedListeners.clear();
};
